import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Button, Callout, Flex, Switch, Text, Tooltip } from 'core/components';
import { Field, FormErrorsButton, InputGroup } from 'core/form';
import SidebarSection from './SidebarSection';
import CollapsedPanelStrip from './panels/ude/CollapsedPanelStrip';
import { useAutoApply } from './panels/ude/useAutoApply';

const AutoApplySwitch = observer(() => {
  const [autoApply, setAutoApply] = useAutoApply();
  return (
    <Tooltip content="Auto-apply query on changes">
      <Switch
        ariaLabel="Auto-apply query on changes"
        checked={autoApply}
        onChange={() => setAutoApply(!autoApply)}
        m={0}
      />
    </Tooltip>
  );
});

@observer
class PanelEditSidebar extends Component {
  render() {
    const {
      form,
      panel,
      panelTypeFields,
      formComponent: PanelFormComponent,
      onRunQuery,
      onSave,
      onCancel,
      onReset,
      onToggleLoading,
      collapsed,
      onToggleCollapse,
      queryResetSignal,
      rowFiltersPending,
      discardSignal
    } = this.props;

    if (collapsed && onToggleCollapse) {
      return <CollapsedPanelStrip label="Query" direction="left" onClick={onToggleCollapse} />;
    }

    const measurement = form.hasField('config.data.measurement') ? form.getValue('config.data.measurement') : null;
    const showApplyButtons = !!measurement;

    return (
      <Flex
        className="bp4-elevation-0 ude-panel-edit-sidebar"
        bg="cardBackground"
        flexDirection="column"
        height="100%"
        position="relative"
        overflow="hidden"
        m="12px 12px 12px 0"
        borderRadius="0 6px 6px 0"
      >
        {!PanelFormComponent && panelTypeFields && (
          <Flex flex={1} flexDirection="column" style={{ overflowY: 'auto', scrollbarWidth: 'thin', minHeight: 0 }}>
            <SidebarSection title={`Options (${panel.get('type')})`} defaultIsOpen>
              <Flex flexDirection="column" gap={2} pt={1} px={2}>
                {Object.keys(panelTypeFields).map((fieldName) => {
                  const field = panelTypeFields[fieldName];
                  const InputComponent = field.formComponent || InputGroup;
                  const { options } = field;
                  return (
                    <Field key={fieldName} name={fieldName} large m={0} options={options}>
                      <InputComponent {...field.formComponentProps} />
                    </Field>
                  );
                })}
              </Flex>
            </SidebarSection>
          </Flex>
        )}

        {PanelFormComponent && (
          <Flex flex={1} flexDirection="column" style={{ overflowY: 'auto', scrollbarWidth: 'thin', minHeight: 0 }}>
            <PanelFormComponent
              key={panel.optimisticId}
              form={form}
              panel={panel}
              onToggleLoading={onToggleLoading}
              onRunQuery={onRunQuery}
              onReset={onReset}
              onSave={onSave}
              onToggleCollapse={onToggleCollapse}
              queryResetSignal={queryResetSignal}
              discardSignal={discardSignal}
            />
          </Flex>
        )}

        {showApplyButtons && (
          <>
            {rowFiltersPending && (
              <Callout intent="primary" p={1} m="12px 12px 0" width="auto" flexShrink={0}>
                <Text as="div" small>
                  Queries are not automatically executed when including or excluding rows. All subsequent changes made
                  will not be applied until you confirm below.
                </Text>
              </Callout>
            )}
            <Flex gap={1} p="12px" flexShrink={0} borderTop="thin" alignItems="center">
              {form.dirty && form.errors.length > 0 && (
                <Flex alignItems="center" color="danger" fontSize={12} fontWeight="bold" whiteSpace="nowrap">
                  {form.errors.length} Error{form.errors.length > 1 ? 's' : ''}
                </Flex>
              )}
              {onCancel && !(panel.board.singlePanelView && panel.hasOnlyDraftVersion) && (
                <Button text="Cancel" onClick={onCancel} />
              )}
              <FormErrorsButton
                fill
                button={
                  <Button
                    disabled={form?.errors?.length > 0}
                    text="Apply"
                    onClick={onRunQuery}
                    intent="primary"
                    fill
                    fontWeight="bold"
                    iconSize={16}
                  />
                }
                form={form}
                errors={form.errors}
              />
              <AutoApplySwitch />
            </Flex>
          </>
        )}
      </Flex>
    );
  }
}

export default PanelEditSidebar;
